import { DefaultServicesDto } from 'src/dtos/system-configuration.dto';
import { getBlockCategoriesListByVersion, getManagedDomainsCountByVersion } from './versionUtils';
import { FwVersion } from 'src/dtos/version.dto';
import { LevelName, ProtectionPageName, ServiceActionType } from './enums';
import { RootStore } from 'src/components/store';
import { ServiceAndStatus } from '../dtos/service-with-status.dto';
import { SystemConfiguration } from './system-configuration';
import { RuleHelper } from './rule-helper';

export const calcBlockInfo = (store: RootStore) =>
    (protectionPage: ProtectionPageName, fwVersion: FwVersion | undefined) => {
        if (!fwVersion) return '';

        const { GeoFiltering, WebFiltering, AppsServices, IPControl, ProtectionPlus } = ProtectionPageName;
        switch (protectionPage) {
            case GeoFiltering: return geoFilterText(RuleHelper.createCountriesWithStatusMap(fwVersion));
            case WebFiltering: return webText(getBlockCategoriesListByVersion(fwVersion), getManagedDomainsCountByVersion(fwVersion) || 0);
            case AppsServices: return servicesStatusText(SystemConfiguration.getInstance().defaultServiceDtoList, store.ruleStore.defaultServicesWithStatus, fwVersion);
            case IPControl: return ipText(RuleHelper.getAllowList(fwVersion), RuleHelper.getBlockList(fwVersion));
            case ProtectionPlus: return protectinoPlusText(fwVersion, store.customerStore.selectedLocation?.serviceLevel);

            default: return '';
        }
    };

const geoFilterText = (countriesWithStatusMap: Record<string, Record<string, boolean>>) => {
    const [outboundCount, inboundCount] = Object.values(countriesWithStatusMap)
        .reduce(([o, i], item) => [o + (item.isOutbound ? 1 : 0), i + (item.isInbound ? 1 : 0)], [0, 0]);

    return `${formatCountText(outboundCount, 'country', 'countries')} blocked from my network\n` +
        `${formatCountText(inboundCount, 'country', 'countries')} blocked from internet`;
};

const webText = (urlFilteringBlockCategoriesList: any[], managedDomainsCount: number) =>
    `${formatCountText(urlFilteringBlockCategoriesList.length, 'category', 'categories')} blocked\n` +
    `${formatCountText(managedDomainsCount, 'domain', 'domains')} managed`;


const ipText = (getAllowList: any[], getBlockList: any[]) =>
    `${formatCountText(getAllowList.length, 'IP', 'IPs')} specified in the allow list\n${formatCountText(getBlockList.length, 'IP', 'IPs')} specified in the block list`;

const servicesStatusText = (defaultServiceDtoList: DefaultServicesDto[], services: ServiceAndStatus[], fwVersion: FwVersion | undefined) => {
    if (!fwVersion) return '';
    const isDirdAllow = (serviceDirState: ServiceActionType) => serviceDirState === ServiceActionType.BLOCK;
    const [outboundCount, inboundCount] =
        services.reduce(([o, i], item) => [o + (isDirdAllow(item.outboundState) ? 1 : 0), i + (isDirdAllow(item.inboundState)  ? 1 : 0)], [0, 0]);
    return `${formatCountText(outboundCount, 'service', 'services')} blocked from my network\n` +
        `${formatCountText(inboundCount, 'service', 'services')} blocked from internet`;
};

const protectinoPlusText = (fwVersion: FwVersion | undefined, serviceLevel?: LevelName) => {
    if (!fwVersion) return '';

    let activeAndInactiveText = '';
    if (serviceLevel === LevelName.GOLD) {
        const stateOfFeatures = RuleHelper.calculateFeaturesState(fwVersion);
        const states = Object.values(stateOfFeatures).map(_ => _);
        const inactiveCount = states.filter(_ => _ === 'Inactive').length;
        const activeCount = states.filter(_ => _ === 'Block' || _ === 'Alert').length;
        activeAndInactiveText = `${formatCountText(inactiveCount, ' Protection profile is', ' Protection profiles are')} Inactive\n` +
            `${formatCountText(activeCount, ' Protection profile is', ' Protection profiles are')} active`;
    }

    return `Dynamic Defense Shield is Active\n` + activeAndInactiveText;
};

function formatCountText(count: number, singularText: string, pluralText: string) {
    if (count === 0) {
        return `No ${pluralText}`;
    } else if (count === 1) {
        return `1 ${singularText}`;
    } else {
        return `${count} ${pluralText}`;
    }
}
